import React from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  useTheme,
  useMediaQuery,
  Button,
  AppBar,
  Toolbar,
  Menu,
  MenuItem,
  IconButton,
} from '@mui/material';
import {
  EventAvailable as EventIcon,
  Storefront as StoreIcon,
  Speed as SpeedIcon,
  LocationOn as LocationIcon,
  Group as CommunityIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import PublicFooter from '../components/layout/PublicFooter';

const ForVendors = () => {
  const theme = useTheme();
  const history = useHistory();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuAnchor, setMobileMenuAnchor] = React.useState(null);

  const handleMobileMenuOpen = (event) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleNavigation = (path) => {
    history.push(path);
    handleMobileMenuClose();
  };

  const benefits = [
    {
      icon: <EventIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: 'All Markets in One Place',
      description: 'No more checking Facebook, Instagram, and websites separately. See all the markets and events happening near you in one spot.',
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: 'Apply in Seconds',
      description: 'Your info is saved, so applying to events is quick and easy. More time for you, less time filling out forms.',
    },
    {
      icon: <StoreIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: 'Show Off What You Do',
      description: 'Create a profile that showcases your services, menu items, and what makes you special. Let organizers see what you bring to the table.',
    },
    {
      icon: <LocationIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: 'Stay Local',
      description: 'Find opportunities right in your community. Connect with markets and events happening in your area.',
    },
    {
      icon: <CommunityIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: 'Never Miss Out',
      description: 'Get notified when new markets pop up near you. Never miss an opportunity to connect with your community.',
    },
  ];

  const features = [
    {
      title: 'Your Profile, Your Way',
      description: 'Update your menu, pricing, and what you offer whenever you want. Everything in one simple place.',
    },
    {
      title: 'Know Right Away',
      description: 'Get instant updates about new events and find out immediately if you got accepted.',
    },
    {
      title: 'See What Works',
      description: 'Track which events bring you the most customers and help you grow.',
    },
    {
      title: 'Community Connection',
      description: 'Connect with other vendors in your area and share experiences.',
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#ffffff' }}>
      {/* Header */}
      <AppBar 
        position="static" 
        elevation={0}
        sx={{ 
          bgcolor: 'white',
          borderBottom: '1px solid rgba(0,0,0,0.08)',
        }}
      >
        <Toolbar sx={{ py: 1 }}>
          <Box
            onClick={() => history.push('/')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              flexGrow: 1,
            }}
          >
            <EventIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: theme.palette.primary.main,
                fontSize: '1.5rem',
              }}
            >
              Vendoroll
            </Typography>
          </Box>
          
          {/* Desktop Navigation */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, alignItems: 'center' }}>
            <Button
              color="inherit"
              onClick={() => history.push('/for-vendors')}
              sx={{ color: 'text.primary', textTransform: 'none', fontWeight: 500 }}
            >
              For Vendors
            </Button>
            <Button
              color="inherit"
              onClick={() => history.push('/for-organizers')}
              sx={{ color: 'text.primary', textTransform: 'none', fontWeight: 500 }}
            >
              For Organizers
            </Button>
            <Button
              color="inherit"
              onClick={() => history.push('/about')}
              sx={{ color: 'text.primary', textTransform: 'none', fontWeight: 500 }}
            >
              About
            </Button>
            <Button
              variant="outlined"
              onClick={() => history.push('/login')}
              sx={{
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                textTransform: 'none',
                fontWeight: 600,
                ml: 1,
                '&:hover': {
                  borderColor: theme.palette.primary.dark,
                  bgcolor: 'rgba(255, 152, 0, 0.04)',
                },
              }}
            >
              Sign In
            </Button>
            <Button
              variant="contained"
              onClick={() => history.push('/register')}
              sx={{
                bgcolor: theme.palette.primary.main,
                color: 'white',
                textTransform: 'none',
                fontWeight: 600,
                ml: 1,
                '&:hover': {
                  bgcolor: theme.palette.primary.dark,
                },
              }}
            >
              Get Started
            </Button>
          </Box>
          
          {/* Mobile Navigation */}
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              color="inherit"
              onClick={handleMobileMenuOpen}
              sx={{ color: 'text.primary' }}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={mobileMenuAnchor}
              open={Boolean(mobileMenuAnchor)}
              onClose={handleMobileMenuClose}
            >
              <MenuItem onClick={() => handleNavigation('/for-vendors')}>For Vendors</MenuItem>
              <MenuItem onClick={() => handleNavigation('/for-organizers')}>For Organizers</MenuItem>
              <MenuItem onClick={() => handleNavigation('/about')}>About</MenuItem>
              <MenuItem onClick={() => handleNavigation('/login')}>Sign In</MenuItem>
              <MenuItem onClick={() => handleNavigation('/register')}>Get Started</MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #ff9800 0%, #ff5722 100%)',
          color: 'white',
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3.5rem' },
                  lineHeight: 1.2,
                }}
              >
                For Vendors
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  mb: 4,
                  fontWeight: 400,
                  lineHeight: 1.6,
                  fontSize: { xs: '1rem', sm: '1.1rem', md: '1.5rem' },
                }}
              >
                We get it—keeping track of all the markets and events in your area is exhausting. That's why we built this for you. All your local opportunities, all in one place, so you can focus on what you love doing instead of searching for where to do it.
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => history.push('/register')}
                endIcon={<ArrowForwardIcon />}
                fullWidth={isMobile}
                sx={{
                  bgcolor: 'white',
                  color: theme.palette.primary.main,
                  px: { xs: 3, md: 4 },
                  py: { xs: 1.25, md: 1.5 },
                  borderRadius: 2,
                  fontSize: { xs: '0.95rem', md: '1.1rem' },
                  fontWeight: 600,
                  textTransform: 'none',
                  minHeight: { xs: 48, md: 56 },
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                  },
                }}
              >
                Get Started Free
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: 3,
                  p: 4,
                  backdropFilter: 'blur(10px)',
                }}
              >
                <StoreIcon sx={{ fontSize: 120, color: 'white', opacity: 0.3, mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'white' }}>
                  Your Vendor Profile
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                  Show off what you do, see all your applications, and find new markets to join—all from one simple place.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Benefits Section */}
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 12 } }}>
        <Typography
          variant="h2"
          component="h2"
          align="center"
          sx={{
            mb: 2,
            color: 'text.primary',
            fontWeight: 700,
            fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' },
          }}
        >
          Why Vendors Love Vendoroll
        </Typography>
        <Typography
          variant="h6"
          align="center"
          color="text.secondary"
          sx={{ 
            mb: { xs: 4, md: 6 }, 
            maxWidth: 700, 
            mx: 'auto',
            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
            px: { xs: 2, md: 0 },
          }}
        >
          Built by vendors, for vendors. We understand what you need because we've been there too.
        </Typography>
        <Grid container spacing={{ xs: 3, md: 4 }}>
          {benefits.map((benefit, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  bgcolor: '#fafafa',
                  borderRadius: 3,
                  border: '1px solid rgba(0,0,0,0.06)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: { xs: 'none', md: 'translateY(-8px)' },
                    boxShadow: { xs: '0 4px 20px rgba(0,0,0,0.08)', md: '0 12px 40px rgba(0,0,0,0.1)' },
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                <Box sx={{ mb: { xs: 2, md: 3 }, '& svg': { fontSize: { xs: 32, md: 40 } } }}>
                  {benefit.icon}
                </Box>
                <Typography 
                  variant="h6" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 600,
                    fontSize: { xs: '1.1rem', md: '1.25rem' },
                  }}
                >
                  {benefit.title}
                </Typography>
                <Typography 
                  color="text.secondary" 
                  sx={{ 
                    lineHeight: 1.7,
                    fontSize: { xs: '0.875rem', md: '0.9375rem' },
                  }}
                >
                  {benefit.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Features Section */}
      <Box sx={{ bgcolor: '#fafafa', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            component="h2"
            align="center"
            sx={{
              mb: { xs: 4, md: 6 },
              color: 'text.primary',
              fontWeight: 700,
              fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' },
            }}
          >
            Simple Tools for Your Community
          </Typography>
          <Grid container spacing={{ xs: 3, md: 4 }}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 3, md: 4 },
                    height: '100%',
                    bgcolor: 'white',
                    borderRadius: 3,
                    border: '1px solid rgba(0,0,0,0.06)',
                  }}
                >
                  <Typography 
                    variant="h6" 
                    gutterBottom 
                    sx={{ 
                      fontWeight: 600, 
                      mb: 2,
                      fontSize: { xs: '1.1rem', md: '1.25rem' },
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography 
                    color="text.secondary" 
                    sx={{ 
                      lineHeight: 1.7,
                      fontSize: { xs: '0.875rem', md: '0.9375rem' },
                    }}
                  >
                    {feature.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: 'linear-gradient(135deg, #ff9800 0%, #ff5722 100%)',
          color: 'white',
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography 
              variant="h2" 
              gutterBottom 
              sx={{ 
                fontWeight: 700, 
                mb: 3,
                fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' },
              }}
            >
              Ready to Join Your Community?
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 4, 
                opacity: 0.95,
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                px: { xs: 2, md: 0 },
              }}
            >
              Stop searching and start connecting. Join local vendors across Canada who are already using Vendoroll.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => history.push('/register')}
              endIcon={<ArrowForwardIcon />}
              fullWidth={isMobile}
              sx={{
                bgcolor: 'white',
                color: theme.palette.primary.main,
                px: { xs: 3, md: 5 },
                py: { xs: 1.25, md: 1.5 },
                borderRadius: 2,
                fontSize: { xs: '0.95rem', md: '1.1rem' },
                fontWeight: 600,
                textTransform: 'none',
                minHeight: { xs: 48, md: 56 },
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.95)',
                },
              }}
            >
              Get Started Free
            </Button>
          </Box>
        </Container>
      </Box>
      <PublicFooter />
    </Box>
  );
};

export default ForVendors;
